import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { NovelService, Novel, NovelCharacter } from '../../services/novelService';
import PageHeader from '../common/PageHeader';
import MetadataGenerationModal from './MetadataGenerationModal';
import './CharactersListEditor.css';

interface CharactersListEditorProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const CharactersListEditor: React.FC<CharactersListEditorProps> = ({ user, onClose, onToggleSidebar }) => {
  const { novelId } = useParams<{ novelId: string }>();
  const navigate = useNavigate();
  
  const [novel, setNovel] = useState<Novel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [characters, setCharacters] = useState<NovelCharacter[]>([]);
  
  // Generation modal state
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState<any[] | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  useEffect(() => {
    if (novelId) {
      loadNovel();
    }
  }, [novelId]);

  const loadNovel = async () => {
    if (!novelId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await NovelService.getNovel(novelId);
      setNovel(data);
      setCharacters(data.metadata?.characters || []);
    } catch (err) {
      console.error('Error loading novel:', err);
      setError('Failed to load novel');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/novel/${novelId}/metadata`);
  };

  const handleAddCharacter = () => {
    const newCharacters = [...characters, { name: '', description: '', role: 'other' as const }];
    setCharacters(newCharacters);
    saveCharacters(newCharacters);
    // Navigate to the new character editor
    navigate(`/novel/${novelId}/metadata/characters/${newCharacters.length - 1}`);
  };

  const handleEditCharacter = (index: number) => {
    navigate(`/novel/${novelId}/metadata/characters/${index}`);
  };

  const handleDeleteCharacter = async (index: number) => {
    if (!window.confirm('Are you sure you want to delete this character?')) return;
    
    const newCharacters = characters.filter((_, i) => i !== index);
    setCharacters(newCharacters);
    await saveCharacters(newCharacters);
  };

  const saveCharacters = async (updatedCharacters: NovelCharacter[]) => {
    if (!novelId || !novel) return;

    try {
      const metadata = { ...novel.metadata, characters: updatedCharacters };
      await NovelService.updateNovelMetadata(novelId, novel.genre || '', metadata);
    } catch (err) {
      console.error('Error saving characters:', err);
      setError('Failed to save characters');
    }
  };

  const handleOpenGenerationModal = () => {
    setGeneratedData(null);
    setGenerationError(null);
    setShowGenerationModal(true);
  };

  const handleGenerateFromChapters = async (chapterIds: string[]) => {
    if (!novelId) return;

    try {
      setIsGenerating(true);
      setGenerationError(null);
      
      const result = await NovelService.generateMetadataFromChapters(
        novelId,
        chapterIds,
        'characters'
      );

      setGeneratedData(result.characters || []);
    } catch (err: any) {
      console.error('Error generating metadata:', err);
      setGenerationError(err.message || 'Failed to generate metadata');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAcceptGenerated = async (data: any[]) => {
    setCharacters(data);
    await saveCharacters(data);
    setShowGenerationModal(false);
    setGeneratedData(null);
  };

  const getDescriptionPreview = (description: string) => {
    if (!description || description.trim() === '') return '(No description)';
    return description.length > 120 ? description.substring(0, 120) + '...' : description;
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      protagonist: 'Protagonist',
      antagonist: 'Antagonist',
      supporting: 'Supporting',
      other: 'Other'
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <div className="characters-list-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
        />
        <div className="characters-list-editor-loading">Loading characters...</div>
      </div>
    );
  }

  if (error || !novel) {
    return (
      <div className="characters-list-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
        />
        <div className="characters-list-editor-error">
          {error || 'Novel not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="characters-list-editor-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="page-header-center-title">{novel.title} /characters</h1>
        }
        leftActions={
          <button 
            className="characters-list-back-btn"
            onClick={handleBack}
          >
            ← Back
          </button>
        }
        rightActions={
          <div className="characters-list-header-actions">
            <button 
              className="characters-list-generate-btn"
              onClick={handleOpenGenerationModal}
              disabled={!novel?.chapters || novel.chapters.length === 0}
              title="Generate characters from chapters"
            >
              ✨ Generate
            </button>
            <button 
              className="characters-list-add-btn"
              onClick={handleAddCharacter}
            >
              + New Character
            </button>
          </div>
        }
      />

      <div className="characters-list-editor-content">
        {characters.length === 0 ? (
          <div className="characters-list-empty">
            <p>No characters yet</p>
            <button className="characters-list-add-btn-large" onClick={handleAddCharacter}>
              + Add First Character
            </button>
          </div>
        ) : (
          <div className="characters-list">
            {characters.map((character, index) => (
              <div key={index} className="character-list-item">
                <div className="character-list-item-header">
                  <div className="character-list-item-info">
                    <span className="character-list-item-name">
                      {character.name || '(Unnamed character)'}
                    </span>
                    <span className={`character-list-item-role role-${character.role}`}>
                      {getRoleLabel(character.role)}
                    </span>
                  </div>
                  <button
                    className="character-list-delete-btn"
                    onClick={() => handleDeleteCharacter(index)}
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
                <p className="character-list-item-description">
                  {getDescriptionPreview(character.description)}
                </p>
                <button
                  className="character-list-edit-btn"
                  onClick={() => handleEditCharacter(index)}
                >
                  Edit
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Generation Modal */}
      <MetadataGenerationModal
        isOpen={showGenerationModal}
        onClose={() => setShowGenerationModal(false)}
        fieldType="characters"
        chapters={novel?.chapters || []}
        currentData={characters}
        onGenerate={handleGenerateFromChapters}
        onAccept={handleAcceptGenerated}
        generatedData={generatedData}
        isGenerating={isGenerating}
        error={generationError}
      />
    </div>
  );
};

export default CharactersListEditor;

